import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AppType, ButtonActivity, DonationItem, ENVIRONMENT, Environment, InputActivity, Lead, PageActivity } from '@domains';
import { ItemService } from '@rspl-api';
import { Designable, DesignService, FxLayoutAlignDirective, FxLayoutDirective, FxLayoutGapDirective, ResponsiveService } from '@rspl-ui';
import { debounceTime, distinctUntilChanged, filter, forkJoin, Observable, switchMap, take, tap, timer } from 'rxjs';

import { AddCustomItemDialogComponent } from '../add-custom-item-dialog/add-custom-item-dialog.component';
import { ItemPreviewDialogComponent } from '../item-preview-dialog/item-preview-dialog.component';

export interface ItemSearchDialogData {
  lead: Lead;
  restrictedItems?: string[];
  createLeadActivity?: (
    leadId: string | undefined | null,
    type: PageActivity | ButtonActivity | InputActivity,
    value?: any,
    request_id?: string,
  ) => Observable<any>;
  updateCart: (lead: Lead) => Observable<Lead>;
}

@Component({
  selector: 'app-item-search-dialog',
  templateUrl: './item-search-dialog.component.html',
  styleUrls: ['./item-search-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    FxLayoutDirective,
    FxLayoutGapDirective,
    MatIcon,
    FxLayoutAlignDirective,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatProgressSpinner,
    NgClass,
  ],
  animations: [
    trigger('contentHeight', [
      state('loading', style({ height: '160px' })),
      state('results', style({ height: '*' })),
      transition('loading <=> results', animate('300ms ease-in-out')),
    ]),
  ],
})
export class ItemSearchDialogComponent extends Designable implements OnInit {
  contentState: 'loading' | 'results' = 'loading';
  searchControl = new FormControl('');
  isLoading = false;
  isDonor: boolean;
  result: {
    query: string;
    small: boolean;
    items: DonationItem[];
    small_items: DonationItem[];
    embedding: {
      split_chance: number;
      column_name: string;
      model_name: string;
      name_weight: number;
      description_weight: number;
      min_distance: number;
    };
    small_calculation: {
      max_small_distance: number;
      mean_items_distance: number;
    };
    x_request_id: string;
  };

  public constructor(
    public reference: MatDialogRef<ItemSearchDialogComponent>,
    @Inject(ENVIRONMENT) protected environment: Environment,
    protected override designService: DesignService,
    public override responsiveService: ResponsiveService,
    private itemService: ItemService,
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ItemSearchDialogData,
  ) {
    super(designService, responsiveService);
    this.isDonor = this.environment.app === AppType.DONOR;
  }

  override ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((value: string) => {
          if (!value || value.trim() === '') {
            this.isLoading = false;
            this.resetResult();
          }
        }),
        filter((value: string) => value.length >= 2),
        tap(() => {
          this.isLoading = true;
          this.contentState = 'loading';
        }),
        switchMap((term: string) => forkJoin([this.itemService.search(term, this.data.lead), timer(500)])),
      )
      .subscribe({
        next: ([data]) => {
          this.result = data;
          this.isLoading = false;
          this.contentState = 'results';
          if (this.data.createLeadActivity)
            this.data
              .createLeadActivity(this.data.lead?.id, ButtonActivity.DONATION_ITEM_SEARCH_RESULTS, undefined, this.result.x_request_id)
              .pipe(take(1))
              .subscribe();
        },
        error: (err) => {
          console.error('Search error', err);
          this.isLoading = false;
          this.contentState = 'results';
        },
      });
  }

  private resetResult(): void {
    this.result = {
      query: '',
      small: false,
      items: [],
      small_items: [],
      embedding: {
        split_chance: 0,
        column_name: '',
        model_name: '',
        name_weight: 0,
        description_weight: 0,
        min_distance: 0,
      },
      small_calculation: {
        max_small_distance: 0,
        mean_items_distance: 0,
      },
      x_request_id: '',
    };
  }

  previewItem(item: DonationItem) {
    this.dialog
      .open(ItemPreviewDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        panelClass: 'item-preview-dialog',
        data: {
          item: item,
          request_id: this.result.x_request_id,
          lead: this.data.lead,
          createLeadActivity: this.data.createLeadActivity,
          updateCart: this.data.updateCart,
          restrictedItems: this.data.restrictedItems,
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.reference.close(true);
        }
      });
    if (this.data.createLeadActivity)
      this.data
        .createLeadActivity(this.data.lead?.id, ButtonActivity.ITEM_PREVIEW, item.id, this.result.x_request_id)
        .pipe(take(1))
        .subscribe();
  }

  addCustomItem() {
    if (this.data.createLeadActivity)
      this.data.createLeadActivity(this.data.lead?.id, ButtonActivity.CUSTOM_ITEM_CREATE).pipe(take(1)).subscribe();
    this.dialog
      .open(AddCustomItemDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        panelClass: 'item-preview-dialog',
        autoFocus: false,
        data: {
          lead: this.data.lead,
          updateCart: this.data.updateCart,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.reference.close(true);
        }
      });
  }
}
